import axios from 'axios';

// Create an axios instance with default config
const api = axios.create({
    baseURL: '/api', // Always use proxy
    timeout: 120000,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },
    withCredentials: false
});

// API service with chat endpoints only
export const apiService = {
    // Chat endpoints
    async sendQuery(question, deepThink, ollamaModel) {
        try {
            const token = localStorage.getItem(process.env.REACT_APP_TOKEN_STORAGE_KEY);
            
            // Log the full request configuration
            const requestConfig = {
                url: '/query',
                method: 'POST',
                baseURL: api.defaults.baseURL,
                headers: {
                    ...api.defaults.headers,
                    Authorization: token ? `Bearer ${token}` : undefined
                },
                data: {
                    question,
                    deep_think: deepThink,
                    ollama_model: ollamaModel
                }
            };
            
            console.log('Making API request with config:', requestConfig);

            const response = await api.post('/query', {
                question,
                deep_think: deepThink,
                ollama_model: ollamaModel
            });

            console.log('API Response received:', {
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
                data: response.data
            });

            if (!response.data) {
                throw new Error('No data received from server');
            }

            return response.data;
        } catch (error) {
            // Enhanced error logging
            console.error('API Error Details:', {
                message: error.message,
                code: error.code,
                name: error.name,
                stack: error.stack,
                response: error.response ? {
                    status: error.response.status,
                    statusText: error.response.statusText,
                    headers: error.response.headers,
                    data: error.response.data
                } : 'No response',
                request: error.request ? {
                    method: error.request.method,
                    url: error.request.url,
                    headers: error.request.headers
                } : 'No request',
                config: error.config ? {
                    url: error.config.url,
                    method: error.config.method,
                    baseURL: error.config.baseURL,
                    headers: error.config.headers,
                    data: error.config.data
                } : 'No config'
            });

            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                throw new Error(
                    error.response.data?.message || 
                    `Server error: ${error.response.status} - ${error.response.statusText}`
                );
            } else if (error.request) {
                // The request was made but no response was received
                if (error.code === 'ECONNABORTED') {
                    throw new Error('Request timed out. The server took too long to respond.');
                } else if (error.code === 'ERR_NETWORK') {
                    throw new Error('Network error. Please check your connection to the server.');
                } else {
                    throw new Error(`No response received from server: ${error.message}`);
                }
            } else {
                // Something happened in setting up the request that triggered an Error
                throw new Error(`Request setup error: ${error.message}`);
            }
        }
    }
};

// Add request interceptor for authentication
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem(process.env.REACT_APP_TOKEN_STORAGE_KEY);
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        console.log('Request interceptor - Final config:', {
            url: config.url,
            method: config.method,
            headers: config.headers,
            baseURL: config.baseURL
        });
        return config;
    },
    (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Add response interceptor for error handling
api.interceptors.response.use(
    (response) => {
        console.log('Response interceptor - Success:', {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
            data: response.data
        });
        return response;
    },
    (error) => {
        console.error('Response interceptor error:', {
            message: error.message,
            status: error.response?.status,
            data: error.response?.data,
            headers: error.response?.headers
        });

        if (error.response?.status === 401) {
            // Handle unauthorized access
            localStorage.removeItem(process.env.REACT_APP_TOKEN_STORAGE_KEY);
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

export default api; 